import { Button, Drawer, Form, Grid, Steps, theme, Typography } from "antd";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import GetPaidFormOrganizationInfo from "./OrganizationInfoStep";
import GetPaidFormAutomationStep from "./AutomationStep";
import GetPaidFormActivationStep from "./ActivationStep";
import { useTranslation } from "react-i18next";
import { useGetPaidInitialData, useFormSubmission } from "hooks/get_paid";
import { useIntegrationMixpanelEvent } from "hooks/useMixpanel";
import { GET_PAID_STATUS, GetPaidData, GetPaidRawFormData } from "types/get_paid";
import { FormInstance } from "antd/lib";
import Feedback from "./Feedback";
import ContactUs from "../Integrations/ContactUs";
import { GetPaidFormProvider } from "contexts/get_paid";

const GetPaidFormDrawer = ({
  handleFormSubmission,
  form,
  isLoading,
}: {
  handleFormSubmission: () => void;
  form: FormInstance<GetPaidRawFormData>;
  isLoading: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const { token } = theme.useToken();
  const { i18n, t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [shouldGoNext, setShouldGoNext] = useState(false);
  const screens = Grid.useBreakpoint();

  const drawerWidth = screens.lg ? "43%" : "100%";

  const { data } = useGetPaidInitialData({ isOpen: open });

  const getPaidInitialData: GetPaidData | undefined = data?.getPaidData;
  const getPaidStatus: GET_PAID_STATUS | undefined = data?.getPaidStatus;

  const handleShouldGoNext = useCallback((shouldGoNext: boolean) => {
    setShouldGoNext(shouldGoNext);
  }, []);

  const { triggerMixpanelEvent } = useIntegrationMixpanelEvent({
    shouldFetchSubscriptionDetails: open,
  });

  const steps = useMemo(
    () => [
      {
        title: (
          <Typography.Text>
            {t(
              "templates.integrations.getpaid_activation.organization_info.title"
            )}
          </Typography.Text>
        ),
        content: (
          <GetPaidFormOrganizationInfo
            getPaidInitialData={getPaidInitialData}
            getPaidStatus={getPaidStatus}
            handleShouldGoNext={handleShouldGoNext}
          />
        ),
      },
      {
        title: (
          <Typography.Text>
            {t(
              "templates.integrations.getpaid_activation.automation_setup.title"
            )}
          </Typography.Text>
        ),
        content: (
          <GetPaidFormAutomationStep
            getPaidInitialData={getPaidInitialData}
            getPaidStatus={getPaidStatus}
            handleShouldGoNext={handleShouldGoNext}
          />
        ),
      },
      {
        title: (
          <Typography.Text>
            {t("templates.integrations.getpaid_activation.activation.title")}
          </Typography.Text>
        ),
        content: (
          <GetPaidFormActivationStep handleShouldGoNext={handleShouldGoNext} />
        ),
      },
    ],
    [getPaidInitialData, handleShouldGoNext]
  );

  const onClose = () => {
    setOpen(false);
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      onClose();
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setShouldGoNext(false);
    } else {
      triggerMixpanelEvent("AR_GP_Step3_Finshed", () => {
        form.submit();
      });
    }
  };

  useEffect(() => {
    document.addEventListener("triggerGetPaidDrawer", handleOpen);
    return () => {
      document.removeEventListener("triggerGetPaidDrawer", handleOpen);
    };
  }, []);

  return (
    <Form
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      form={form}
      onFinish={handleFormSubmission}
      preserve={true}
    >
      <Drawer
        open={open}
        onClose={onClose}
        width={drawerWidth}
        title={t("templates.integrations.getpaid_activation.title")}
        placement={i18n.language === "ar" ? "left" : "right"}
        footer={
          <>
            <div
              style={{ display: "flex", gap: token.marginXS, width: "100%" }}
            >
              <Button
                style={{ flex: 1, paddingBlock: token.paddingMD }}
                onClick={handlePrevious}
                disabled={isLoading}
              >
                {currentStep === 0
                  ? t("forms.buttons.cancel")
                  : t("forms.buttons.previous")}
              </Button>
              <Button
                style={{ flex: 1, paddingBlock: token.paddingMD }}
                type="primary"
                onClick={handleNext}
                loading={isLoading}
                disabled={!shouldGoNext || isLoading}
              >
                {currentStep === 2
                  ? t("forms.buttons.finish_activate")
                  : t("forms.buttons.next")}
              </Button>
            </div>
            <ContactUs />
          </>
        }
      >
        <div
          style={{
            paddingInline: token.paddingLG,
            paddingBlock: token.paddingSM,
            borderColor: token.colorBorder,
            borderWidth: token.lineWidth,
            borderStyle: "solid",
            borderRadius: 30,
          }}
        >
          <Steps current={currentStep} items={steps} />
        </div>

        <div style={{ paddingBlock: token.paddingMD }}>
          <GetPaidFormProvider>
            {steps[currentStep].content}
          </GetPaidFormProvider>
        </div>
      </Drawer>
    </Form>
  );
};

const GetPaidDrawer = () => {
  const [form] = Form.useForm<GetPaidRawFormData>();
  const { handleFormSubmission, isSuccess, isError, isLoading } =
    useFormSubmission({ form });

  if (isSuccess && !isLoading) {
    return <Feedback shouldOpen={true} status="success" />;
  }

  if (isError && !isLoading) {
    return <Feedback shouldOpen={true} status="error" />;
  }

  return (
    <GetPaidFormDrawer
      handleFormSubmission={handleFormSubmission}
      form={form}
      isLoading={isLoading}
    />
  );
};

export default GetPaidDrawer;
